import { normalizePhrase } from './routines.js';
/**
 * Wake word detection on top of a speech-to-text transcript ("Hej Elena, pusti Cecu").
 * Recognizers rarely write the name the same way twice: "Jelena", "Helena", "Alena", "Eleno",
 * "hej lena", "Hejelena". Anything one letter away from Elena / Jelena / Helena counts, minus
 * real words that are that close ("zelena", "jelen"). Cyrillic transcripts ("Хеј Елена") work
 * too, since matching goes through normalizePhrase.
 */
const baseNames = ['elena', 'jelena', 'helena'];
const greetings = new Set([
    'hej',
    'ej',
    'hey',
    'aj',
    'e',
    'hei',
    'haj',
    'hi',
    'hallo',
    'hello',
    'ok',
    'okay',
]);
/** Words one letter away from the name that people actually say. */
const notTheName = new Set([
    'zelena',
    'zeleno',
    'zelene',
    'zeleni',
    'selena',
    'jelen',
    'jeleni',
    'jelenu',
    'jelenom',
    'jelene',
    'plena',
    'helen',
]);
function editDistanceAtMostOne(a, b) {
    if (Math.abs(a.length - b.length) > 1)
        return false;
    let i = 0;
    let j = 0;
    let edits = 0;
    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) {
            i++;
            j++;
            continue;
        }
        if (++edits > 1)
            return false;
        if (a.length > b.length)
            i++;
        else if (a.length < b.length)
            j++;
        else {
            i++;
            j++;
        }
    }
    return edits + (a.length - i) + (b.length - j) <= 1;
}
/** One normalized word that is (a misheard) Elena. */
export function isWakeName(word) {
    if (word.length < 4 || word.length > 8 || notTheName.has(word))
        return false;
    if (baseNames.includes(word))
        return true;
    // "hejelena", "ejlena": the greeting glued to the name
    for (const g of ['hej', 'hey', 'ej', 'aj']) {
        if (word.startsWith(g) && word.length > g.length + 3) {
            const rest = word.slice(g.length);
            if (rest === 'lena' || baseNames.some((n) => editDistanceAtMostOne(rest, n)))
                return true;
        }
    }
    return word.length >= 5 && baseNames.some((n) => editDistanceAtMostOne(word, n));
}
/**
 * Finds the last "Elena" in the transcript and returns the words after it.
 * "Hej Elena pusti Cecu" → { heard: true, command: "pusti Cecu" }.
 * A trailing "Elena" ("pusti Cecu, Elena") also counts: the words before it become the command.
 */
export function detectWake(transcript) {
    const words = transcript.trim().split(/\s+/).filter(Boolean);
    const plain = words.map((w) => normalizePhrase(w));
    let at = -1;
    for (let i = plain.length - 1; i >= 0; i--) {
        const word = plain[i] ?? '';
        // "hej lena": the recognizer split the name, the greeting in front makes it safe
        const splitName = (word === 'lena' || word === 'lina') && greetings.has(plain[i - 1] ?? '');
        if (isWakeName(word) || splitName) {
            at = i;
            break;
        }
    }
    if (at === -1)
        return { heard: false, command: '' };
    const tidy = (list) => list
        .join(' ')
        .replace(/^[\s,.!?;:–-]+|[\s,.;:–-]+$/g, '')
        .trim();
    let command = tidy(words.slice(at + 1));
    if (!command && at > 0) {
        // "Pusti Cecu, Elena": command before the name, minus a leading greeting.
        let start = 0;
        while (start < at && greetings.has(plain[start] ?? ''))
            start++;
        command = tidy(words.slice(start, at));
    }
    // A bare greeting left over ("hej") is not a command.
    if (normalizePhrase(command).length < 2 || greetings.has(normalizePhrase(command)))
        command = '';
    return { heard: true, command };
}
/**
 * Same over every alternative the recognizer offers (best first): the name is often only in
 * the second or third guess. The best alternative that has the name wins.
 */
export function detectWakeAny(transcripts) {
    for (const t of transcripts) {
        const wake = detectWake(t);
        if (wake.heard)
            return wake;
    }
    return { heard: false, command: '' };
}
/** Words to bias a recognizer that supports it (iOS, Android 13+) toward the name. */
export const wakeHints = ['Elena', 'Hej Elena'];
//# sourceMappingURL=wake.js.map